<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import PipelineCard from './PipelineCard.vue';
import { CRM_LANES, CRM_STAGE_ATTRIBUTE_KEY, CRM_STAGES } from './constants';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');
const conversationAttributes = useMapGetter(
  'attributes/getConversationAttributes'
);
const currentUser = useMapGetter('getCurrentUser');

const statusFilter = ref('open');
const inboxFilter = ref('');
const definitionsLoaded = ref(false);
const creatingAttribute = ref(false);
const setupError = ref('');
const moveError = ref('');
const draggingConversation = ref(null);
const requestVersion = ref(0);
const movingIds = ref(new Set());
const boardLoading = ref(false);

const createLaneState = () => ({
  conversations: [],
  count: 0,
  page: 1,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: false,
});

const laneState = reactive(
  Object.fromEntries(CRM_LANES.map(lane => [lane.id, createLaneState()]))
);

const currentRole = computed(() =>
  getUserRole(currentUser.value || { accounts: [] }, accountId.value)
);
const isAdministrator = computed(() => currentRole.value === 'administrator');
const stageDefinition = computed(() =>
  conversationAttributes.value.find(
    attribute => attribute.attributeKey === CRM_STAGE_ATTRIBUTE_KEY
  )
);
const expectedStageValues = CRM_STAGES.map(stage => stage.value);
const isStageDefinitionCompatible = computed(() => {
  const definition = stageDefinition.value;
  if (!definition) return false;
  if (definition.attributeDisplayType !== 'list') return false;
  const configuredValues = definition.attributeValues || [];
  return expectedStageValues.every(value => configuredValues.includes(value));
});
const canCreateStageDefinition = computed(
  () =>
    definitionsLoaded.value && !stageDefinition.value && isAdministrator.value
);
const showAdminRequired = computed(
  () =>
    definitionsLoaded.value && !stageDefinition.value && !isAdministrator.value
);
const hasStageDefinitionConflict = computed(
  () =>
    definitionsLoaded.value &&
    stageDefinition.value &&
    !isStageDefinitionCompatible.value
);
const stageOptions = computed(() =>
  CRM_LANES.map(lane => ({
    value: lane.id,
    stageValue: lane.value,
    label: t(lane.labelKey),
  }))
);
const translatedLanes = computed(() =>
  CRM_LANES.map(lane => ({
    ...lane,
    label: t(lane.labelKey),
    ...laneState[lane.id],
  }))
);
const statusOptions = computed(() => [
  { value: 'all', label: t('CRM_PIPELINE.FILTERS.ALL_STATUSES') },
  { value: 'open', label: t('CRM_PIPELINE.STATUS.OPEN') },
  { value: 'pending', label: t('CRM_PIPELINE.STATUS.PENDING') },
  { value: 'resolved', label: t('CRM_PIPELINE.STATUS.RESOLVED') },
]);
const inboxOptions = computed(() => [
  { id: '', name: t('CRM_PIPELINE.FILTERS.ALL_INBOXES') },
  ...inboxes.value.map(inbox => ({ id: String(inbox.id), name: inbox.name })),
]);

const inboxNameFor = conversation =>
  inboxes.value.find(
    inbox => Number(inbox.id) === Number(conversation.inbox_id)
  )?.name || '';

const buildFilters = lane => {
  const filters = [
    {
      attribute_key: CRM_STAGE_ATTRIBUTE_KEY,
      custom_attribute_type: 'conversation_attribute',
      filter_operator: lane.value ? 'equal_to' : 'is_not_present',
      values: lane.value ? [lane.value] : [],
    },
  ];

  if (statusFilter.value !== 'all') {
    filters.push({
      attribute_key: 'status',
      custom_attribute_type: '',
      filter_operator: 'equal_to',
      values: [statusFilter.value],
    });
  }

  if (inboxFilter.value) {
    filters.push({
      attribute_key: 'inbox_id',
      custom_attribute_type: '',
      filter_operator: 'equal_to',
      values: [Number(inboxFilter.value)],
    });
  }

  return {
    payload: filters.map((filter, index) => ({
      ...filter,
      query_operator: index === filters.length - 1 ? null : 'and',
    })),
  };
};

const fetchLane = async (lane, version, page = 1, append = false) => {
  const state = laneState[lane.id];
  if (append) state.loadingMore = true;
  else state.loading = true;
  state.error = false;

  try {
    const response = await ConversationAPI.filter({
      queryData: buildFilters(lane),
      page,
      sortBy: 'last_activity_at_desc',
    });
    if (version !== requestVersion.value) return;

    const result = response.data || {};
    const records = result.payload || [];
    const meta = result.meta || {};
    const uniqueRecords = append
      ? [
          ...state.conversations,
          ...records.filter(
            record =>
              !state.conversations.some(existing => existing.id === record.id)
          ),
        ]
      : records;

    state.conversations = uniqueRecords;
    state.count = Number(meta.all_count ?? meta.count ?? uniqueRecords.length);
    state.page = page;
    state.hasMore = uniqueRecords.length < state.count;
  } catch {
    if (version === requestVersion.value) state.error = true;
  } finally {
    if (version === requestVersion.value) {
      state.loading = false;
      state.loadingMore = false;
    }
  }
};

const refreshLane = async (laneId, version = requestVersion.value) => {
  const lane = CRM_LANES.find(item => item.id === laneId);
  if (!lane) return;
  laneState[laneId].conversations = [];
  laneState[laneId].count = 0;
  laneState[laneId].page = 1;
  laneState[laneId].hasMore = false;
  await fetchLane(lane, version);
};

const refreshBoard = async () => {
  if (!isStageDefinitionCompatible.value) return;
  const version = ++requestVersion.value;
  boardLoading.value = true;
  CRM_LANES.forEach(({ id }) => {
    const state = laneState[id];
    state.conversations = [];
    state.count = 0;
    state.page = 1;
    state.hasMore = false;
    state.loading = false;
    state.loadingMore = false;
    state.error = false;
  });

  for (let index = 0; index < CRM_LANES.length; index += 4) {
    await Promise.all(
      CRM_LANES.slice(index, index + 4).map(lane => fetchLane(lane, version))
    );
  }
  if (version === requestVersion.value) boardLoading.value = false;
};

const loadMore = async lane => {
  if (lane.loadingMore || !lane.hasMore) return;
  await fetchLane(lane, requestVersion.value, lane.page + 1, true);
};

const createStageDefinition = async () => {
  creatingAttribute.value = true;
  setupError.value = '';
  try {
    await store.dispatch('attributes/create', {
      attribute_display_name: 'Etapa CRM Kevoa',
      attribute_description: 'Etapa no funil comercial Kevoa.',
      attribute_model: 'conversation_attribute',
      attribute_display_type: 'list',
      attribute_key: CRM_STAGE_ATTRIBUTE_KEY,
      attribute_values: expectedStageValues,
    });
    await store.dispatch('attributes/get');
    if (isStageDefinitionCompatible.value) await refreshBoard();
  } catch (error) {
    setupError.value = error?.message || t('CRM_PIPELINE.SETUP.ERROR');
  } finally {
    creatingAttribute.value = false;
  }
};

const openConversation = conversation => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: conversation.id,
    },
  });
};

const moveConversation = async (conversation, targetStageId) => {
  if (movingIds.value.has(conversation.id)) return;
  const targetLane = CRM_LANES.find(lane => lane.id === targetStageId);
  if (!targetLane) return;

  const sourceValue =
    conversation.custom_attributes?.[CRM_STAGE_ATTRIBUTE_KEY] || null;
  if (sourceValue === targetLane.value) return;

  movingIds.value = new Set(movingIds.value).add(conversation.id);
  moveError.value = '';
  try {
    if (targetLane.value) {
      await ConversationAPI.updateCustomAttributes({
        conversationId: conversation.id,
        customAttributes: { [CRM_STAGE_ATTRIBUTE_KEY]: targetLane.value },
        merge: true,
      });
    } else {
      await ConversationAPI.destroyCustomAttributes({
        conversationId: conversation.id,
        customAttributes: [CRM_STAGE_ATTRIBUTE_KEY],
      });
    }

    conversation.custom_attributes = {
      ...(conversation.custom_attributes || {}),
    };
    if (targetLane.value) {
      conversation.custom_attributes[CRM_STAGE_ATTRIBUTE_KEY] =
        targetLane.value;
    } else {
      delete conversation.custom_attributes[CRM_STAGE_ATTRIBUTE_KEY];
    }

    const sourceLane =
      CRM_LANES.find(lane => lane.value === sourceValue) || CRM_LANES[0];
    const version = requestVersion.value;
    await Promise.all([
      refreshLane(sourceLane.id, version),
      refreshLane(targetLane.id, version),
    ]);
  } catch {
    moveError.value = t('CRM_PIPELINE.MOVE.ERROR');
  } finally {
    const nextMovingIds = new Set(movingIds.value);
    nextMovingIds.delete(conversation.id);
    movingIds.value = nextMovingIds;
  }
};

const onDrop = lane => {
  if (!draggingConversation.value) return;
  moveConversation(draggingConversation.value, lane.id);
  draggingConversation.value = null;
};

watch([statusFilter, inboxFilter], () => refreshBoard());

onMounted(async () => {
  const results = await Promise.allSettled([
    store.dispatch('attributes/get'),
    store.dispatch('inboxes/get'),
  ]);
  definitionsLoaded.value = true;
  const failed = results.some(result => result.status === 'rejected');
  if (failed) setupError.value = t('CRM_PIPELINE.SETUP.LOAD_ERROR');
  if (isStageDefinitionCompatible.value) await refreshBoard();
});
</script>

<template>
  <main
    class="flex h-full min-h-0 flex-col bg-kevoa-navy-50 dark:bg-kevoa-deep-page"
  >
    <header
      class="flex flex-wrap items-center justify-between gap-4 border-b border-kevoa-ink-200 bg-white px-6 py-5 dark:border-kevoa-deep-border-subtle dark:bg-kevoa-deep-card"
    >
      <div>
        <p
          class="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-kevoa-navy-700 dark:text-kevoa-neon-500"
        >
          <span class="size-2 rounded-full bg-kevoa-neon-500" />
          {{ t('CRM_PIPELINE.EYEBROW') }}
        </p>
        <h1
          class="mt-1 text-xl font-semibold text-kevoa-navy-900 dark:text-white"
        >
          {{ t('CRM_PIPELINE.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted">
          {{ t('CRM_PIPELINE.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <label class="sr-only" for="crm-status-filter">
          {{ t('CRM_PIPELINE.FILTERS.STATUS_LABEL') }}
        </label>
        <select
          id="crm-status-filter"
          v-model="statusFilter"
          :disabled="movingIds.size > 0"
          class="h-9 rounded-lg border border-kevoa-ink-300 bg-white px-3 text-sm text-kevoa-navy-900 outline-none focus:border-kevoa-navy-700 focus-visible:ring-2 focus-visible:ring-kevoa-neon-500 dark:border-kevoa-deep-border-strong dark:bg-kevoa-deep-control dark:text-white dark:focus:border-kevoa-neon-500"
        >
          <option
            v-for="option in statusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <label class="sr-only" for="crm-inbox-filter">
          {{ t('CRM_PIPELINE.FILTERS.INBOX_LABEL') }}
        </label>
        <select
          id="crm-inbox-filter"
          v-model="inboxFilter"
          :disabled="movingIds.size > 0"
          class="h-9 max-w-56 rounded-lg border border-kevoa-ink-300 bg-white px-3 text-sm text-kevoa-navy-900 outline-none focus:border-kevoa-navy-700 focus-visible:ring-2 focus-visible:ring-kevoa-neon-500 dark:border-kevoa-deep-border-strong dark:bg-kevoa-deep-control dark:text-white dark:focus:border-kevoa-neon-500"
        >
          <option
            v-for="inbox in inboxOptions"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <Button
          :label="t('CRM_PIPELINE.ACTIONS.REFRESH')"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="outline"
          size="sm"
          class="!outline-kevoa-ink-300 !text-kevoa-navy-700 hover:!bg-kevoa-navy-50 dark:!outline-kevoa-deep-border-strong dark:!text-white dark:hover:!bg-kevoa-deep-subtle"
          :is-loading="boardLoading"
          :disabled="!isStageDefinitionCompatible || movingIds.size > 0"
          @click="refreshBoard"
        />
      </div>
    </header>

    <div
      v-if="setupError"
      class="mx-6 mt-4 rounded-lg border border-kevoa-warning-500 bg-kevoa-warning-100 px-4 py-3 text-sm text-kevoa-ink-900 dark:border-kevoa-deep-border-strong dark:bg-kevoa-deep-card dark:text-kevoa-neon-300"
      role="alert"
    >
      {{ setupError }}
    </div>
    <div
      v-if="moveError"
      class="mx-6 mt-4 rounded-lg border border-kevoa-danger-500 bg-kevoa-danger-100 px-4 py-3 text-sm text-kevoa-ink-900 dark:border-kevoa-deep-border-strong dark:bg-kevoa-deep-card dark:text-kevoa-danger-500"
      role="alert"
    >
      {{ moveError }}
    </div>

    <section
      v-if="!definitionsLoaded"
      class="grid flex-1 place-items-center p-8 text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted"
      aria-live="polite"
    >
      {{ t('CRM_PIPELINE.SETUP.LOADING') }}
    </section>

    <section
      v-else-if="canCreateStageDefinition"
      class="grid flex-1 place-items-center p-8"
    >
      <div class="max-w-lg text-center">
        <span
          class="i-lucide-kanban mx-auto mb-4 block size-10 text-kevoa-navy-700 dark:text-kevoa-neon-500"
        />
        <h2 class="text-lg font-semibold text-kevoa-navy-900 dark:text-white">
          {{ t('CRM_PIPELINE.SETUP.TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted">
          {{ t('CRM_PIPELINE.SETUP.DESCRIPTION') }}
        </p>
        <Button
          class="mt-5 !bg-kevoa-neon-500 !text-kevoa-navy-900 hover:!bg-kevoa-neon-600"
          :label="t('CRM_PIPELINE.SETUP.ACTION')"
          icon="i-lucide-settings-2"
          :is-loading="creatingAttribute"
          :disabled="creatingAttribute"
          @click="createStageDefinition"
        />
      </div>
    </section>

    <section
      v-else-if="showAdminRequired"
      class="grid flex-1 place-items-center p-8"
    >
      <div class="max-w-lg text-center">
        <span
          class="i-lucide-shield-alert mx-auto mb-4 block size-10 text-kevoa-warning-500"
        />
        <h2 class="text-lg font-semibold text-kevoa-navy-900 dark:text-white">
          {{ t('CRM_PIPELINE.SETUP.ADMIN_REQUIRED_TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted">
          {{ t('CRM_PIPELINE.SETUP.ADMIN_REQUIRED_DESCRIPTION') }}
        </p>
      </div>
    </section>

    <section
      v-else-if="hasStageDefinitionConflict"
      class="grid flex-1 place-items-center p-8"
    >
      <div class="max-w-lg text-center">
        <span
          class="i-lucide-triangle-alert mx-auto mb-4 block size-10 text-kevoa-warning-500"
        />
        <h2 class="text-lg font-semibold text-kevoa-navy-900 dark:text-white">
          {{ t('CRM_PIPELINE.SETUP.CONFLICT_TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted">
          {{ t('CRM_PIPELINE.SETUP.CONFLICT_DESCRIPTION') }}
        </p>
      </div>
    </section>

    <section
      v-else
      class="flex min-h-0 flex-1 gap-3 overflow-x-auto p-4 md:p-6"
    >
      <article
        v-for="lane in translatedLanes"
        :key="lane.id"
        class="flex h-full max-h-full w-72 shrink-0 flex-col rounded-xl border border-kevoa-ink-200 bg-kevoa-navy-50 dark:border-kevoa-deep-border-subtle dark:bg-kevoa-deep-subtle"
        :class="{
          'border-kevoa-navy-500 ring-1 ring-kevoa-navy-500':
            draggingConversation,
        }"
        @dragover.prevent
        @drop.prevent="onDrop(lane)"
      >
        <header
          class="flex items-center justify-between gap-2 border-b border-kevoa-ink-200 px-3 py-3 dark:border-kevoa-deep-border-subtle"
        >
          <h2
            class="truncate text-sm font-semibold text-kevoa-navy-900 dark:text-white"
          >
            {{ lane.label }}
          </h2>
          <span
            class="rounded-full bg-kevoa-navy-100 px-2 py-0.5 text-xs text-kevoa-navy-700 dark:bg-kevoa-deep-card dark:text-kevoa-deep-body"
          >
            {{ lane.count }}
          </span>
        </header>

        <div class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
          <div
            v-if="lane.loading && !lane.conversations.length"
            class="grid flex-1 place-items-center text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted"
            aria-live="polite"
          >
            {{ t('CRM_PIPELINE.LANE.LOADING') }}
          </div>
          <div
            v-else-if="lane.error && !lane.conversations.length"
            class="grid flex-1 place-items-center gap-2 p-3 text-center text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted"
            role="alert"
          >
            <span>{{ t('CRM_PIPELINE.LANE.ERROR') }}</span>
            <Button
              :label="t('CRM_PIPELINE.ACTIONS.RETRY')"
              color="slate"
              variant="outline"
              size="xs"
              class="!outline-kevoa-ink-300 !text-kevoa-navy-700 hover:!bg-kevoa-navy-50 dark:!outline-kevoa-deep-border-strong dark:!text-white dark:hover:!bg-kevoa-deep-subtle"
              @click="refreshLane(lane.id)"
            />
          </div>
          <div
            v-else-if="!lane.conversations.length"
            class="grid flex-1 place-items-center px-3 text-center text-sm text-kevoa-ink-500 dark:text-kevoa-deep-muted"
          >
            {{ t('CRM_PIPELINE.LANE.EMPTY') }}
          </div>

          <PipelineCard
            v-for="conversation in lane.conversations"
            :key="conversation.id"
            :conversation="conversation"
            :stage-options="stageOptions"
            :stage-attribute-key="CRM_STAGE_ATTRIBUTE_KEY"
            :inbox-name="inboxNameFor(conversation)"
            :is-moving="movingIds.size > 0"
            @open="openConversation"
            @stage-change="
              moveConversation($event.conversation, $event.stageId)
            "
            @drag-start="draggingConversation = $event"
            @drag-end="draggingConversation = null"
          />

          <div
            v-if="lane.error && lane.conversations.length"
            class="rounded-lg bg-kevoa-danger-100 p-2 text-xs text-kevoa-danger-500 dark:bg-kevoa-deep-card"
            role="alert"
          >
            {{ t('CRM_PIPELINE.LANE.ERROR') }}
          </div>
          <Button
            v-if="lane.hasMore"
            class="w-full !outline-kevoa-ink-300 !text-kevoa-navy-700 hover:!bg-kevoa-navy-50 dark:!outline-kevoa-deep-border-strong dark:!text-white dark:hover:!bg-kevoa-deep-subtle"
            :label="t('CRM_PIPELINE.LANE.LOAD_MORE')"
            color="slate"
            variant="outline"
            size="sm"
            :is-loading="lane.loadingMore"
            @click="loadMore(lane)"
          />
        </div>
      </article>
    </section>
  </main>
</template>
